import { InputNumber } from "@/components/InputNumber";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
} from "@/utils/componentUtils";
import { toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, DatePicker, Form, Row } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Rule } from "antd/lib/form";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import LabelWithTooltip from "../../../../../../common/components/form/labels/LabelWithTooltip";
import { rowGutter } from "../../../../../../common/constants";
import { CreateUpdateContractGainerRecord, CreateUpdateThirdPillarContract } from "../../../../types";
import { calculateContractStatus, calculateGainerRecordStartDates } from "../../../../utils";
import ContractStatusTag from "../../../tags/ContractStatusTag";

const getTransferredToOtherBrokerDateRules = (
  signDate?: string,
  cancellationDate?: string,
  transferredToOtherBroker?: boolean,
) => {
  const rules: Rule[] = [validations.none];

  if (signDate) {
    rules.push(validations.notSameOrBefore(signDate, t("contract.attrs.signDate")));
  }
  if (cancellationDate) {
    rules.push(validations.notAfter(cancellationDate, t("contract.attrs.cancellationDate")));
  }
  if (transferredToOtherBroker) {
    rules.push(validations.notNull);
  }

  return rules;
};

const colSpan = 4;

export const ThirdPillarContractFormDataSection = () => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<CreateUpdateThirdPillarContract>();
  const status = Form.useWatch("status", form);
  const signDate = Form.useWatch("signDate", form);
  const cancellationDate = Form.useWatch("cancellationDate", form);
  const transferredToOtherBroker = Form.useWatch("transferredToOtherBroker", form);

  const handleSignDateChange = () => {
    handleStatusDefiningDateChange();
    const signDate = form.getFieldValue(["signDate"]) as string;
    const records = form.getFieldValue(["gainerRecords"]) as CreateUpdateContractGainerRecord[];
    form.setFieldsValue({ gainerRecords: calculateGainerRecordStartDates(records, signDate) });
  };

  const handleStatusDefiningDateChange = () => {
    const { signDate, cancellationDate, transferredToOtherBrokerDate } = form.getFieldsValue([
      ["signDate"],
      ["cancellationDate"],
      ["transferredToOtherBrokerDate"],
    ]) as CreateUpdateThirdPillarContract;
    form.setFieldsValue({
      status: calculateContractStatus({
        startDate: signDate,
        endDate: undefined,
        cancellationDate: cancellationDate,
        transferredToOtherBrokerDate: transferredToOtherBrokerDate,
      }),
    });
  };

  const handleTransferredToOtherBrokerChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      form.setFieldsValue({ transferredToOtherBrokerDate: undefined });
      handleStatusDefiningDateChange();
    }
  };

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("contract.sections.contractData")}
      extra={status ? <ContractStatusTag status={status} /> : undefined}
    >
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="mediationReportSignDate"
            label={t("contract.attrs.mediationReportSignDate")}
            rules={[
              validations.notNull,
              signDate ? validations.notAfter(signDate, t("contract.attrs.signDate")) : validations.none,
            ]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} maxDate={toDate(signDate)} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="signDate"
            label={t("contract.attrs.signDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} onChange={handleSignDateChange} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="cancellationDate"
            label={t("contract.attrs.cancellationDate")}
            rules={[signDate ? validations.notBefore(signDate, t("contract.attrs.signDate")) : validations.none]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              minDate={toDate(signDate)}
              onChange={handleStatusDefiningDateChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="annualPremium"
            label={t("contract.attrs.annualPremium")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="basePremium"
            label={t("contract.attrs.basePremium")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="transferredFromOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredFromOtherBroker")}
                tooltip={t("contract.helpers.transferredFromOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }} onChange={handleTransferredToOtherBrokerChange}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBroker")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBrokerDate"
            label={
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBrokerDate")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDateDesc")}
              />
            }
            rules={getTransferredToOtherBrokerDateRules(signDate, cancellationDate, transferredToOtherBroker)}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              disabled={!transferredToOtherBroker}
              minDate={toDate(signDate)?.add(1, "day")}
              maxDate={toDate(cancellationDate)}
              onChange={handleStatusDefiningDateChange}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter} className="margin-top-small">
        <Col span={24}>
          <Form.Item
            name="note"
            label={t("contract.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
